export interface RoleHolder {
  role?: string;
  isGemeindeAdmin?: boolean;
  adminGemeindeIds?: string[];
  isVereinAdmin?: boolean;
  isCompanyAdmin?: boolean;
  isShopAdmin?: boolean;
}

export const isPlatformAdminUser = (u: RoleHolder | null | undefined): boolean =>
  u?.role === 'PLATFORM_ADMIN';

export const isGemeindeAdminUser = (u: RoleHolder | null | undefined): boolean =>
  !!u?.isGemeindeAdmin;

export const isVereinAdminUser = (u: RoleHolder | null | undefined): boolean => !!u?.isVereinAdmin;

export const isCompanyAdminUser = (u: RoleHolder | null | undefined): boolean =>
  !!u?.isCompanyAdmin;

export const isShopAdminUser = (u: RoleHolder | null | undefined): boolean => !!u?.isShopAdmin;

export const canAccessAdminUser = (u: RoleHolder | null | undefined): boolean =>
  isPlatformAdminUser(u) ||
  isGemeindeAdminUser(u) ||
  isVereinAdminUser(u) ||
  isCompanyAdminUser(u) ||
  isShopAdminUser(u);

/** Whether `orgId` is one of the org ids in `adminIds` — e.g. `u.adminShopIds`, `u.adminVereinIds`. */
export const isAdminOfOrgId = (
  adminIds: string[] | undefined,
  orgId: string | null | undefined,
): boolean => !!orgId && !!adminIds?.includes(orgId);

/**
 * Whether the viewer manages a post: either they are the literal creator, or `isOrgAdmin`
 * says they administer the org the post was made on behalf of. Mirrors the backend's
 * `PostConversationAccess.canManage` so web and backend apply the same ownership rule —
 * shared by the Marktplatz and Hilfe detail pages instead of each reimplementing it.
 */
export const isPostOwnerOrOrgAdmin = (
  literalOwnerId: string | null | undefined,
  currentUserId: string | null | undefined,
  isOrgAdmin: boolean,
): boolean => !!currentUserId && (literalOwnerId === currentUserId || isOrgAdmin);

/**
 * Whether a message/reply was authored by the owning side (the viewer, the post's literal
 * creator, or any other org co-admin) rather than by a buyer/helper.
 *
 * `fromOrgAdmin` (stamped by the backend from the author's actual org membership) is the
 * authoritative signal — it also catches a co-admin's *untargeted* message, sent before they
 * picked a specific helper/buyer, which `recipientUserId` alone would miss (it's only set once
 * a reply targets someone). `recipientUserId` stays as a fallback for older cached data.
 */
export const isOwningSideMessage = (
  message: { userId: string; recipientUserId?: string | null; fromOrgAdmin?: boolean },
  currentUserId: string | null | undefined,
  literalOwnerId: string | null | undefined,
): boolean =>
  message.userId === currentUserId ||
  message.userId === literalOwnerId ||
  message.fromOrgAdmin === true ||
  message.recipientUserId != null;
